import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { environment } from '../../environments/environment';

export interface QueryLimits {
  max_time_seconds: number;
  max_result_rows: number;
  batch_size: number;
}

export interface QueueStatsResponse {
  success: boolean;
  error?: string;
  data: {
    queue_name: string;
    queued_count: number;
    processing_count: number;
    checked_at_epoch_ms: number;
    oldest_queued_age_seconds: number | null;
    jobs: {
      queued_count: number;
      processing_count: number;
      in_system_count: number;
      oldest_queued_age_seconds: number | null;
      queue_utilization_pct: number;
    };
    workers: {
      operational_count: number;
      processing_count: number;
      idle_count: number;
      configured_processes_per_container: number;
      heartbeat_interval_seconds: number;
      heartbeat_ttl_seconds: number;
      details: Array<{
        worker_id: string;
        hostname: string;
        process_name: string;
        pid: number;
        status: 'idle' | 'processing' | string;
        current_job_id: string | null;
        heartbeat_age_seconds: number;
        uptime_seconds: number;
        current_job_age_seconds: number | null;
        last_seen_epoch_ms: number;
      }>;
    };
    redis: {
      ok: boolean;
      ping_ms: number;
    };
    query_limits?: QueryLimits | null;
    warnings: string[];
  };
}

export type QueueStatsPollEvent =
  | { type: 'data'; response: QueueStatsResponse }
  | { type: 'error'; error: unknown }
  | { type: 'paused' };

@Injectable({ providedIn: 'root' })
export class QueueStatsService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  private readonly errorBackoffDelaysMs = [10_000, 20_000, 30_000] as const;
  private readonly queryLimitsSubject = new BehaviorSubject<QueryLimits | null>(null);

  readonly queryLimits$ = this.queryLimitsSubject.asObservable();

  get currentQueryLimits(): QueryLimits | null {
    return this.queryLimitsSubject.value;
  }

  getQueueStats(): Observable<QueueStatsResponse> {
    const headers = this.buildAuthHeaders();
    return this.http
      .get<QueueStatsResponse>(`${environment.API_BASE_URL}/api/queue/stats`, {
        headers,
      })
      .pipe(tap((response) => this.captureQueryLimits(response)));
  }

  refreshQueryLimits(): Observable<QueryLimits | null> {
    return this.getQueueStats().pipe(map(() => this.currentQueryLimits));
  }

  pollQueueStats(
    visibleIntervalMs = environment.QUEUE_STATS_VISIBLE_POLL_INTERVAL_MS,
  ): Observable<QueueStatsPollEvent> {
    return new Observable<QueueStatsPollEvent>((subscriber) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let inFlightSub: { unsubscribe: () => void } | null = null;
      let errorBackoffIndex = 0;
      let destroyed = false;

      const effectiveIntervalMs = Math.max(250, Number(visibleIntervalMs) || 1000);

      const clearTimer = () => {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      };

      const isVisible = () => {
        if (typeof document === 'undefined') return true;
        return document.visibilityState === 'visible';
      };

      const scheduleNext = (delayMs: number) => {
        if (destroyed) return;
        clearTimer();
        timeoutId = setTimeout(() => {
          timeoutId = null;
          runPoll();
        }, Math.max(0, delayMs));
      };

      const nextBackoffDelay = () => {
        const delay = this.errorBackoffDelaysMs[
          Math.min(errorBackoffIndex, this.errorBackoffDelaysMs.length - 1)
        ];
        errorBackoffIndex = Math.min(errorBackoffIndex + 1, this.errorBackoffDelaysMs.length - 1);
        return delay;
      };

      const runPoll = () => {
        if (destroyed) return;
        if (!isVisible()) {
          subscriber.next({ type: 'paused' });
          return;
        }

        inFlightSub?.unsubscribe();
        inFlightSub = this.getQueueStats().subscribe({
          next: (response) => {
            if (response?.success) {
              errorBackoffIndex = 0;
              subscriber.next({ type: 'data', response });
              scheduleNext(effectiveIntervalMs);
              return;
            }

            subscriber.next({ type: 'error', error: response?.error || 'Queue stats unavailable.' });
            scheduleNext(nextBackoffDelay());
          },
          error: (error) => {
            subscriber.next({ type: 'error', error });
            scheduleNext(nextBackoffDelay());
          },
        });
      };

      const onVisibilityChange = () => {
        if (destroyed) return;
        if (!isVisible()) {
          inFlightSub?.unsubscribe();
          clearTimer();
          subscriber.next({ type: 'paused' });
          return;
        }

        errorBackoffIndex = 0;
        runPoll();
      };

      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVisibilityChange);
      }

      runPoll();

      return () => {
        destroyed = true;
        clearTimer();
        inFlightSub?.unsubscribe();
        if (typeof document !== 'undefined') {
          document.removeEventListener('visibilitychange', onVisibilityChange);
        }
      };
    });
  }

  private buildAuthHeaders(): HttpHeaders {
    const tokens = this.auth.tokens();
    const token = tokens?.idToken || tokens?.accessToken;
    if (!token) return new HttpHeaders();
    return new HttpHeaders({ Authorization: `Bearer ${token}` });
  }

  private captureQueryLimits(response: QueueStatsResponse | null | undefined): void {
    if (!response?.success) return;
    const limits = response.data?.query_limits;
    if (!this.isQueryLimits(limits)) return;
    this.queryLimitsSubject.next(limits);
  }

  private isQueryLimits(value: unknown): value is QueryLimits {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const candidate = value as Partial<QueryLimits>;
    return (
      typeof candidate.max_time_seconds === 'number' &&
      Number.isFinite(candidate.max_time_seconds) &&
      typeof candidate.max_result_rows === 'number' &&
      Number.isFinite(candidate.max_result_rows) &&
      typeof candidate.batch_size === 'number' &&
      Number.isFinite(candidate.batch_size)
    );
  }
}
